import { useEffect, useMemo, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

// ABAC navigation filter hook
import useNavigationFilter from "hooks/useNavigationFilter";
import { useAppMode } from "context/AppModeContext";
import { BACKOFFICE_ORG_ID, SYSTEM_DISPLAY_NAMES, selectUserDetails } from "config/appMode";
// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Skeleton from "@mui/material/Skeleton";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import SidenavCollapse from "./SidenavCollapse";
import SidenavList from "./SidenavList";
import SidenavItem from "./SidenavItem";
import SidenavCard from "./SidenavCard";

// Custom styles for the Sidenav
import SidenavRoot from "./SidenavRoot";
import sidenavLogoLabel from "./styles/sidenav";

import { useSoftUIController, setMiniSidenav } from "context";
import SpaceShip from "pages/components/Icons/SpaceShip";
import Logo from "assets/images/logos/flex2logo.png";
import SubLogo from "assets/images/logos/Logo.png";
import { ADMIN_EMAILS } from "config/constants";

const routePattern = (route) => {
  if (!route) return null;
  const escaped = route.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped.replace(/:[^/]+/g, "[^/]+")}(?:/)?$`);
};

const routeMatches = (route, pathname) => {
  if (!route) return false;
  if (route.includes(":")) return routePattern(route).test(pathname);
  return pathname === route || pathname.startsWith(`${route}/`);
};

const routeHasActiveChild = (route, pathname) => {
  if (routeMatches(route.route, pathname)) return true;
  return (route.collapse || []).some((child) => routeHasActiveChild(child, pathname));
};

// Sidebar group expansion is persisted in localStorage so it survives the
// sidenav remounting on navigation. Without this, every navigation reset the
// open/closed state and unrelated groups appeared to collapse — the "menu
// mutated and I lost the section" bug. Mirrors EnvironmentalPortalSidebar.
const OPEN_GROUPS_STORAGE = "afmsSidenavOpenGroups";

const readOpenGroups = () => {
  try {
    const raw = window.localStorage.getItem(OPEN_GROUPS_STORAGE);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeOpenGroups = (value) => {
  try {
    window.localStorage.setItem(OPEN_GROUPS_STORAGE, JSON.stringify(value));
  } catch (e) {
    /* localStorage unavailable — expansion just won't persist across reloads */
  }
};

function Sidenav({ color, brand, brandName, routes, onSwitchSystem, ...rest }) {
  // Open groups are sets of keys ({ [key]: true }) — multiple groups can be
  // open at once, and the state is lazy-loaded from localStorage so a group
  // the user expanded stays expanded across navigations.
  const [openCollapse, setOpenCollapse] = useState(() => readOpenGroups().collapse || {});
  const [openNestedCollapse, setOpenNestedCollapse] = useState(
    () => readOpenGroups().nested || {}
  );
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  // The AFMS navy/blue sidebar style is the app-wide standard — applied to the
  // backoffice menu too, not just /afms/* routes.
  const isAfmsSidenav = true;
  const collapseName = pathname.split("/").slice(1)[0];
  const itemName = pathname.split("/").slice(1)[1];

  const { loading: authLoading } = useSelector((state) => state.auth);
  const authUser = useSelector((state) => state.auth.user);
  // Use the shape-tolerant selector — auth.user can be either the
  // login response (which has .userDetails nested) or the post-getUser
  // shape (where the user IS the inner DTO). The earlier raw
  // `state.auth.user?.userDetails` only handled the first case, so
  // sessions that hydrated via getUser got an undefined org and the
  // backoffice filter never fired.
  const userDetails = useMemo(() => selectUserDetails(authUser), [authUser]);
  // True when the host is *.backoffice.<base> — a URL-level signal we
  // can use even before user-info has resolved, so the strip-down
  // filter takes effect on the first paint.
  const isBackofficeHost = useMemo(() => {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname || "";
    const parts = host.split(".");
    return parts.length > 1 && parts[0].toLowerCase() === "backoffice";
  }, []);
  const isBackofficeContext =
    userDetails?.organizationId === BACKOFFICE_ORG_ID || isBackofficeHost;
  const { defaultRoute } = useAppMode();

  // Label for the system switcher under the logo (e.g. "Fixed Operations",
  // "Lift Connect Pro"). Suppressed for backoffice (org 1) — they have one
  // system, no switching needed.
  const currentSystemLabel = useMemo(() => {
    if (!userDetails) return null;
    if (userDetails.organizationId === BACKOFFICE_ORG_ID) return null;
    const mode = userDetails.effectiveAppMode;
    return SYSTEM_DISPLAY_NAMES[mode] || null;
  }, [userDetails]);

  // Use ABAC-based navigation filtering
  const { routes: filteredRoutes, loading: abacLoading } = useNavigationFilter(routes, {
    useLegacyFallback: true, // Fall back to role/plan checks when ABAC not configured
  });

  // Backoffice-org users (the platform admin org) get a stripped-down nav:
  // only the four backoffice collapses (Organizations, Environmental,
  // LiftConnect Pro, Legacy) plus anything else explicitly tagged with
  // roles: ["SUPER_ADMIN"]. Tenant-facing menus (Dashboard, Assets,
  // Tickets, etc.) are hidden so the backoffice nav stays focused on
  // platform tools, not customer features the admin would never use.
  //
  // Three tenant menus that admins still occasionally need access to —
  // Commerce, Platform Foundation, Leads & Contact — are grafted in as
  // nested sub-collapses under Legacy instead of being hidden outright.
  // No routes.js changes; the same collapse object is just reparented.
  const backofficeFiltered = useMemo(() => {
    if (!isBackofficeContext) return filteredRoutes;
    const PLATFORM_KEYS = new Set([
      "platform-site-builder",
      "platform-health",
      "platform-resources",
      "platform-organizations",
      "platform-environmental",
      "platform-lcp",
      // Devices menu (links out to devices-webapp). Absent from routes.js
      // entirely when REACT_APP_DEVICES_WEBAPP_URL is unset, so listing it
      // here is harmless in environments without devices-webapp.
      "platform-devices",
      "platform-legacy",
    ]);
    // Keys of the tenant top-level collapses we want nested inside Legacy.
    const LEGACY_BUCKET_KEYS = new Set([
      "products1",            // Commerce
      "organizationSettings", // Platform Foundation
      "leadsManagement",      // Leads & Contact
    ]);
    const legacyBuckets = filteredRoutes
      .filter((r) => r.type === "collapse" && LEGACY_BUCKET_KEYS.has(r.key))
      .map((r) => ({
        name: r.name,
        key: `legacy-${r.key}`,
        collapse: r.collapse || [],
      }));

    // Top level: ONLY the 4 platform collapses. The previous fallback
    // ("anything tagged SUPER_ADMIN") was too permissive — tenant menus
    // like Commerce / Platform Foundation / Leads & Contact carry
    // SUPER_ADMIN in their roles array (because admins CAN access them)
    // and were leaking through. They're already grafted under Legacy
    // via legacyBuckets below, so dropping them from the top level
    // doesn't lose any reachability.
    const result = filteredRoutes
      .filter((r) => PLATFORM_KEYS.has(r.key))
      .map((r) => {
        if (r.key === "platform-legacy") {
          return {
            ...r,
            collapse: [...(r.collapse || []), ...legacyBuckets],
          };
        }
        return r;
      });
    return result;
  }, [filteredRoutes, isBackofficeContext]);

  const loading = authLoading || abacLoading;

  // Auto-expand the collapse group whose child route matches the current
  // pathname — merged in, never removed, so it can't collapse a group the
  // user opened themselves.
  useEffect(() => {
    if (!backofficeFiltered.length) return;
    const match = backofficeFiltered.find(
      (r) =>
        r.type === "collapse" &&
        r.collapse &&
        routeHasActiveChild(r, pathname)
    );
    if (match) {
      setOpenCollapse((current) => ({ ...current, [match.key]: true }));
    }
  }, [backofficeFiltered, pathname]);

  // Persist expansion so it survives sidenav remounts and page reloads.
  useEffect(() => {
    writeOpenGroups({ collapse: openCollapse, nested: openNestedCollapse });
  }, [openCollapse, openNestedCollapse]);

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    // A collapse array holds nav items, which require a `name`; skip any
    // route-only/malformed entry so it never reaches SidenavItem.
    const template = collapse
      .filter((item) => item.name)
      .map((item) => {
        const { name, route, key, href, nested: extraIndent, collapse: childCollapse } = item;
        // Items inside a parent collapse always render with the `nested`
        // style (smaller font + 22px margin). If the route entry ALSO
        // sets `nested: true`, push another step deeper via
        // SidenavItem's `extraIndentRem` so it visibly sits one level
        // below its siblings — used to indent AFMS Settings → Devices
        // under AFMS Settings → Assets without restructuring the tree.
        const extraIndentRem = extraIndent ? 1.5 : 0;

        // Recursive branch: a nested item that itself carries a
        // `collapse:` array becomes an expandable SidenavItem. Mirrors
        // the top-level renderCollapse behaviour so navigation trees can
        // go 3+ levels deep (e.g. Settings → LCP Settings → Certificate
        // Setup → Upload Logo). openNestedCollapse is keyed by `name`,
        // so all names in the tree must be unique.
        if (childCollapse) {
          return (
            <SidenavItem
              key={key}
              name={name}
              nested
              extraIndentRem={extraIndentRem}
              isAfms={isAfmsSidenav}
              refKey={key}
              open={Boolean(openNestedCollapse[name])}
              onClick={() =>
                setOpenNestedCollapse((current) => ({
                  ...current,
                  [name]: !current[name],
                }))
              }
            >
              {renderNestedCollapse(childCollapse)}
            </SidenavItem>
          );
        }

        return href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem
            name={name}
            nested
            extraIndentRem={extraIndentRem}
            isAfms={isAfmsSidenav}
            refKey={key}
          />
        </Link>
      ) : (
        <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
          <SidenavItem
            name={name}
            active={routeMatches(route, pathname)}
            nested
            extraIndentRem={extraIndentRem}
            isAfms={isAfmsSidenav}
            refKey={key}
          />
        </NavLink>
      );
    });

    return template;
  };

  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses
      .filter((item) => item.name)
      .map(({ name, collapse, route, href, key, nested }) => {
      let returnValue;
      const active = isAfmsSidenav
        ? routeHasActiveChild({ route, collapse }, pathname)
        : key === itemName;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            name={name}
            active={active}
            open={Boolean(openNestedCollapse[name])}
            isAfms={isAfmsSidenav}
            refKey={key}
            onClick={() =>
              setOpenNestedCollapse((current) => ({ ...current, [name]: !current[name] }))
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        // Leaf items inside a top-level collapse are submenu items, so they
        // always render with the `nested` (indented) style — regardless of
        // whether the route sets its own `nested` flag. Without this they sat
        // flush-left with their parent collapse.
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
            <SidenavItem name={name} active={active} nested isAfms={isAfmsSidenav} refKey={key} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem
              name={name}
              active={active}
              nested
              extraIndentRem={nested ? 1.5 : 0}
              isAfms={isAfmsSidenav}
              refKey={key}
            />
          </NavLink>
        );
      }
      return (
        <SidenavList key={key} isAfms={isAfmsSidenav}>
          {returnValue}
        </SidenavList>
      );
    });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  // Routes are pre-filtered by useNavigationFilter hook using ABAC permissions,
  // then narrowed to platform-only entries when the caller is in the
  // backoffice org (see `backofficeFiltered` above).
  const renderRoutes = backofficeFiltered.map(
    ({
      type,
      name,
      icon,
      title,
      collapse,
      noCollapse,
      key,
      href,
      route,
    }) => {
      let returnValue;
      const active = isAfmsSidenav
        ? routeHasActiveChild({ route, collapse }, pathname)
        : key === collapseName;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={active}
                noCollapse={noCollapse}
                isAfms={isAfmsSidenav}
                refKey={key}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={active}
                isAfms={isAfmsSidenav}
                refKey={key}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={active}
              open={Boolean(openCollapse[key])}
              isAfms={isAfmsSidenav}
              refKey={key}
              onClick={() => setOpenCollapse((current) => ({ ...current, [key]: !current[key] }))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <SoftTypography
            key={key}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={miniSidenav ? 0 : 0.6}
            pl={3}
            mt={2}
            mb={1}
            ml={1}
            sx={{
              color: isAfmsSidenav ? "rgba(255,255,255,0.42)" : undefined,
              fontSize: isAfmsSidenav ? 10 : undefined,
              fontWeight: isAfmsSidenav ? 800 : undefined,
              maxWidth: miniSidenav ? 0 : "100%",
              overflow: "hidden",
              whiteSpace: "nowrap",
              transition: "opacity 0.3s, max-width 0.3s",
            }}
          >
            {title}
          </SoftTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            sx={isAfmsSidenav ? { borderColor: "rgba(255,255,255,0.08)", my: 1 } : undefined}
          />
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, miniSidenav, isAfms: isAfmsSidenav }}
    >
      <SoftBox pt={isAfmsSidenav ? 2.25 : 3} pb={1} px={isAfmsSidenav ? 2 : 4} pr={0} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold", color: isAfmsSidenav ? "#fff" : undefined }}>
              close
            </Icon>
          </SoftTypography>
        </SoftBox>
        {/* Logo → user's home screen. Same NavLink-to-defaultRoute it's
            always been. The system switcher lives below the logo so the
            two actions stay distinct. */}
        <SoftBox component={NavLink} to={defaultRoute} display="flex" alignItems="center">
          {miniSidenav ? (
            <img src={SubLogo} width={60} alt="SubLogo" />
          ) : (
            <img src={Logo} width={160} alt="Logo" />
          )}

          <SoftBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          ></SoftBox>
        </SoftBox>
        {/* System name → opens the system picker so the user can switch.
            Suppressed for backoffice (org 1) since it has nothing else to
            switch to. Suppressed in miniSidenav mode where the chrome
            collapses. */}
        {!miniSidenav && currentSystemLabel && onSwitchSystem ? (
          <SoftBox
            component="button"
            type="button"
            onClick={onSwitchSystem}
            aria-label="Switch to a different system"
            sx={{
              mt: 0.5,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: isAfmsSidenav ? "rgba(255,255,255,0.85)" : "info.main",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              background: "none",
              border: "none",
              p: 0,
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {currentSystemLabel}
          </SoftBox>
        ) : null}
      </SoftBox>
      <Divider sx={isAfmsSidenav ? { borderColor: "rgba(255,255,255,0.08)" } : undefined} />

      {loading ? (
        Array.from({ length: 7 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            height={40}
            animation="wave"
            sx={{
              m: 1,
              borderRadius: 2,
              bgcolor: isAfmsSidenav ? "rgba(255,255,255,0.12)" : undefined,
            }}
          />
        ))
      ) : (
        <List sx={isAfmsSidenav ? { py: 1 } : undefined}>{renderRoutes}</List>
      )}
    </SidenavRoot>
  );
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSwitchSystem: PropTypes.func,
};

export default Sidenav;
